import { getBuyOnceAsSellingPlan, getBuyOnceAsSellingPlanGroup, isBuyOnceId } from '../../models/buy-once'
import { buildElement } from '../../utils/element'
import { widgetState } from '../../store/customizations/index'
import { buildTemplate } from './widgets/template-builder'
import { setSellingPlanChange } from '../../store/selling-plans/actions'
import { tilesTemplate } from '../templates/tiles-template'
import { SellingPlan } from '../../models/selling-plan'
import { buildTilesLabel } from './widgets/tiles-label'
import { Color } from '../../utils/color'
import { appendElementsInOrder, getSelectedGroupIndex, getSellingPlansMainElement, getSellingPlansMainElementWrapper } from './layout'
import { getSellingPlanGroups } from '../../store/selling-plans/getters'
import { getProduct } from '../../store/product/getters'
import { PlanSelectionLayout } from '../../models/layout'
import { groupsHasAnyDiscount } from './widgets/discount'
import { sortStrategy } from '../../services/sort/selling-plans'

export class TileButtons implements PlanSelectionLayout {
  render (): HTMLElement {
    const sellingPlanElements: HTMLElement[] = []

    const hasAnyDiscount = groupsHasAnyDiscount()
    let sellingPlansCounter = 0
    getSellingPlanGroups().forEach(group => {
      const sortingStrategy = sortStrategy(group.id)
      group.sellingPlans
        .sort(sortingStrategy)
        .forEach(plan => {
          const isSelected = getSelectedGroupIndex() === (1 + sellingPlansCounter++)
          sellingPlanElements.push(this.buildSellingPlanElement(plan, isSelected, true, group.id, hasAnyDiscount))
        })
    })
    const mainElement = getSellingPlansMainElement()
    appendElementsInOrder(mainElement, sellingPlanElements, this.buildBuyOnce(hasAnyDiscount))

    const mainElementWrapper = getSellingPlansMainElementWrapper()
    mainElementWrapper.appendChild(mainElement)
    mainElementWrapper.appendChild(this.styles())

    return mainElementWrapper
  }

  private buildSellingPlanElement (plan: SellingPlan, checked: boolean, isSubscription: boolean, groupId: string, hasAnyDiscount: boolean): HTMLElement {
    const planId = (isBuyOnceId(groupId) ? groupId : plan.id).toString()
    const container = buildElement('div', `yotpo-radio-container ${checked ? 'yotpo-selected' : ''}`, {
      'data-seling-plan-group-id': planId
    })
    const radioElementsWrapper = buildElement('div', 'yotpo-radio-button-text-wrapper', {
      'data-seling-plan-group-id': planId
    })

    const groupsTemplate = buildTemplate({
      id: planId,
      value: plan && !isBuyOnceId(planId) ? plan.id : '',
      text: plan.name,
      checked,
      classString: 'yotpo-tile-buttons',
      inputAttributes: {
        data: plan ? plan.name : '',
        name: 'selling_plan'
      }
    },
    tilesTemplate()
    )

    const discountLabel = buildTilesLabel(groupId, plan.id, plan.priceAdjustments, hasAnyDiscount)

    radioElementsWrapper.appendChild(groupsTemplate)
    radioElementsWrapper.appendChild(discountLabel)
    container.appendChild(radioElementsWrapper)

    this.registerForLabelClick(container, plan, groupId)
    this.registerToElementChangeEvent(plan, container, groupsTemplate, isSubscription, groupId)

    return container
  }

  private registerToElementChangeEvent (plan: SellingPlan, element: HTMLElement, container: HTMLElement, isSubscription: boolean, groupId: string): void {
    element.addEventListener('change', (event: Event) => {
      const selectedSellingPlanId = (event.target as HTMLInputElement).value
      const isDiscountAvailable = plan.priceAdjustments ? plan.priceAdjustments.length > 0 : false
      setSellingPlanChange(groupId, selectedSellingPlanId, isSubscription, getProduct().id.toString(), isDiscountAvailable)
    })
  }

  private styles (): any {
    const radioStyle = buildElement('style', 'yotpo-radio-style', {})
    radioStyle.innerHTML = `
    .yotpo-widget-subscriptions-add-to-cart .yotpo-selling-plans {
      width: 100%;
      grid-gap: 10px;
      display: grid;
      color: #444;
      grid-template-columns: 2fr 2fr;
    }
    .yotpo-widget-subscriptions-add-to-cart .yotpo-is-preview .yotpo-selling-plans {
      max-width: 400px;
    }
    .yotpo-widget-subscriptions-add-to-cart .yotpo-selling-plans-wrapper {
      display: block;
      margin: 20px 0;
    }
    .yotpo-widget-subscriptions-add-to-cart .yotpo-selling-plans-wrapper.yotpo-admin-preview {
      text-align: center;
      min-height: auto;
      display: block;
    }
    .yotpo-widget-subscriptions-add-to-cart .yotpo-admin-preview .yotpo-selling-plans {
      grid-gap: 10px;
      display: grid;
      color: #444;
      grid-template-columns: 2fr 2fr;
      margin: auto;
      max-width: 400px;
    }
    .yotpo-widget-subscriptions-add-to-cart {
      -webkit-appearance: none;
      -moz-appearance: none;
      background-repeat: no-repeat;
      background-position-x: 96%;
      background-position-y: 50%;
      background-size: 10%;
      border-radius: 3px;
      margin-right: 2rem;
      padding: 10px;
      padding-right: 20px;
      display: contents;
      border: 1px solid transparent;
    }
    ${widgetState.customizations.tiles.oddSpread ? this.getOddSpreadCss() : ''}
    .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-container {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
    .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-container {
      padding: 10px;
      border: 1px solid #B4B8C7;
      background-color: ${widgetState.customizations.styles.notSelectedRadio.backgroundColor};
      border-radius: 8px;
      height: ${widgetState.customizations.tiles.tileHeight};
    }
    .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-container.yotpo-selected {
      border-style: ${widgetState.customizations.tiles.borderStyle};
      border-width: ${widgetState.customizations.tiles.borderSize};
      border-color: ${widgetState.customizations.styles.radioButton.innerColor};
      background-color: ${this.getBackgroundColorWithOpacity('20')};
      pointer-events: none;
    }
    .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-button-text-wrapper {
      display: flex;
      flex-direction: column;
      align-items: baseline;
    }
    .yotpo-widget-subscriptions-add-to-cart .yotpo-tile-buttons {
      margin: auto;
    }`
    return radioStyle
  }

  private getOddSpreadCss (): string {
    return `.yotpo-widget-subscriptions-add-to-cart .yotpo-radio-container:first-child:nth-last-child(odd) {
      grid-column: auto / span 2;
    }
    .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-container:first-child:nth-last-child(even),
    .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-container:first-child:nth-last-child(even) ~
    .yotpo-widget-subscriptions-add-to-cart .yotpo-radio-container {
      grid-column: auto / span 1;
    }`
  }

  private buildBuyOnce (hasAnyDiscount: boolean): HTMLElement {
    const buyOnceGroup = getBuyOnceAsSellingPlanGroup()
    const buyOncePlan = getBuyOnceAsSellingPlan()
    const isSelected = widgetState.customizations.autoPickOptionIndex === 0
    return this.buildSellingPlanElement(buyOncePlan, isSelected, false, buyOnceGroup.id, hasAnyDiscount)
  }

  private registerForLabelClick (container: HTMLElement, plan: SellingPlan, groupId: string): void {
    container.addEventListener('click', () => {
      document.querySelectorAll('.yotpo-radio-label-input')
        .forEach(element => {
          const value = element.getAttribute('value')
          const isBuyOnce = (!value && isBuyOnceId(groupId))
          if (isBuyOnce || value === plan.id.toString()) {
            (<HTMLElement>element).click()
          }
        })
    })
  }

  private getBackgroundColorWithOpacity (opacityLevel: string): string {
    const color = new Color(widgetState.customizations.styles.selectedRadio.backgroundColor)
    return `${color.toHex()}${opacityLevel}`
  }
}
